import gatewayClient from '@cedalo/webui/src/helper/GatewayClient';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

const PATH = 'shared-machine';

const SHARE_MACHINE = `
mutation ShareMachine($machineId: ID!, $tsExpiry: Float!, $visibility: Visibility!) {
	shareMachine(machineId: $machineId, tsExpiry: $tsExpiry, visibility: $visibility) {
		code,
		success,
		link {
			id
		}
	}
}
`;
const UNSHARE_MACHINE = `
mutation UnshareMachine($machineId: ID!) {
	unshareMachine(machineId: $machineId)
}
`;
const SHARED_MACHINE = `
query SharedMachine($id: ID!, $type: IdType) {
	sharedMachine(id: $id, type: $type) {
		code,
		success,
		link {
			id,
			tsExpiry,
			visibility
		}
	}
}
`;

const DAY = 24 * 60 * 60 * 1000;
const defaultExpiry = () => Date.now() + DAY;
const defaultLink = () => ({
	id: '',
	tsExpiry: defaultExpiry(),
	visibility: 'logged_in'
});

const getHostOrigin = () => {
	if (!window.location) throw new Error('Failed to determine host origin!');
	return window.location.origin;
};
const msToDate = (ms) => (ms > 0 ? new Date(ms) : new Date(defaultExpiry()));
// eslint-disable-next-line no-nested-ternary
const timeReducer = (time, curr, index) => (index === 0 ? curr : index === 1 ? `${time}:${curr}` : time);
const getDate = (ms) =>
	msToDate(ms)
		.toISOString()
		.split('T')[0] || '';
const getTime = (ms) =>
	msToDate(ms)
		.toTimeString()
		.split(':')
		.reduce(timeReducer, '');
const getExpiry = (date, time) => {
	date = date || getDate(defaultExpiry());
	time = time || getTime(defaultExpiry());
	return Date.parse(`${date}T${time}:00`);
};

const queryGraphQL = async (query, variables, onResponse) => {
	try {
		const response = await gatewayClient.graphql(query, variables);
		onResponse(response);
	} catch (error) {
		onResponse(undefined, error);
	}
};

export const ShareMachineDialog = (props) => {
	const { machineId, open, onClose } = props;
	const [lookup, setLookup] = useState(true);
	const [sharedLink, setSharedLink] = useState(defaultLink());

	const updateExpiry = (tsExpiry) => setSharedLink({ ...sharedLink, id: '', tsExpiry });

	const updateVisibility = (ev) => {
		setSharedLink({ ...sharedLink, id: '', visibility: ev.target.value });
	};

	const share = () => {
		const { tsExpiry, visibility } = sharedLink;
		queryGraphQL(SHARE_MACHINE, { machineId, tsExpiry, visibility }, ({ shareMachine = {} } = {}) => {
			const { link, success } = shareMachine;
			if (success) setSharedLink({ ...sharedLink, id: link.id });
		});
	};
	const unshare = () => {
		queryGraphQL(UNSHARE_MACHINE, { machineId }, (data) => {
			if (data) setSharedLink({ ...sharedLink, id: '' });
		});
	};
	const lookupShared = () => {
		if (open) {
			queryGraphQL(SHARED_MACHINE, { id: machineId, type: 'machine' }, ({ sharedMachine } = {}) => {
				const { link, success } = sharedMachine || { success: false };
				// use defaultLink to remove any previous created link which might has been deleted in-between...
				setSharedLink(success ? { ...sharedLink, ...link } : defaultLink());
				setLookup(false);
			});
		}
	};
	useEffect(() => {
		lookupShared();
	}, [open]);

	return (
		<Dialog open={open} onClose={onClose} fullWidth>
			<DialogTitle>
				<FormattedMessage id="Extensions.ShareMachine.Dialog.Title" defaultMessage="Create link to share App" />
			</DialogTitle>
			<DialogContent style={{ paddingTop: '20px' }}>
				{lookup ? (
					<DialogContentText style={{ marginBottom: '5px' }}>
						<FormattedMessage
							id="Extensions.ShareMachine.Dialog.LookupText"
							defaultMessage="Lookup existing link. Please wait..."
						/>
					</DialogContentText>
				) : null}
				<TextField
					variant="outlined"
					size="small"
					margin="normal"
					type="date" // or datetime-local
					disabled={!!sharedLink.id || sharedLink.tsExpiry < 0}
					style={{ width: '35%' }}
					value={getDate(sharedLink.tsExpiry)}
					onChange={(ev) => updateExpiry(getExpiry(ev.target.value, getTime(sharedLink.tsExpiry)))}
					label={
						<FormattedMessage id="Extensions.ShareMachine.Dialog.ExpireDate" defaultMessage="Expiry Date" />
					}
				/>
				<TextField
					variant="outlined"
					size="small"
					margin="normal"
					type="time" // or datetime-local
					disabled={!!sharedLink.id || sharedLink.tsExpiry < 0}
					style={{ width: '35%', marginLeft: '10px' }}
					value={getTime(sharedLink.tsExpiry)}
					onChange={(ev) => updateExpiry(getExpiry(getDate(sharedLink.tsExpiry), ev.target.value))}
					label={
						<FormattedMessage id="Extensions.ShareMachine.Dialog.ExpireTime" defaultMessage="Expiry Time" />
					}
					InputLabelProps={{ shrink: true }}
				/>
				<FormControl margin="normal" style={{ marginLeft: '10px' }}>
					<FormControlLabel
						control={
							<Checkbox
								color="primary"
								checked={sharedLink.tsExpiry < 0}
								onChange={(ev, checked) => updateExpiry(checked ? -1 : defaultExpiry())}
							/>
						}
						disabled={!!sharedLink.id}
						label={
							<Typography color="textSecondary" style={{ lineHeight: 'normal', fontSize: '0.65rem' }}>
								<FormattedMessage
									id="Extensions.ShareMachine.Dialog.ExpireNever"
									defaultMessage="Expire Never"
								/>
							</Typography>
						}
					/>
				</FormControl>
				{/* <div style={{ width: '100%', height: '10px' }} /> */}
				<FormControl margin="normal" style={{ marginTop: '8px', marginBottom: '8px' }}>
					<InputLabel id="visibility-label">
						<FormattedMessage id="Extensions.ShareMachine.Dialog.Visibility" defaultMessage="Share with" />
					</InputLabel>
					<Select
						labelId="visibility-label"
						value={sharedLink.visibility}
						onChange={updateVisibility}
						// input={<Input name="age" id="age-label-placeholder" />}
						// displayEmpty
						disabled={!!sharedLink.id}
						name="visibility"
					>
						<MenuItem value="logged_in">
							<FormattedMessage
								id="Extensions.ShareMachine.Dialog.Visibility.LoggedIn"
								defaultMessage="Streamsheets Users"
							/>
						</MenuItem>
						<MenuItem value="public">
							<FormattedMessage
								id="Extensions.ShareMachine.Dialog.Visibility.Public"
								defaultMessage="Anyone"
							/>
						</MenuItem>
					</Select>
				</FormControl>
				<div style={{ width: '100%', height: '10px' }} />
				{sharedLink.id ? (
					<React.Fragment>
						<Typography
							color="textSecondary"
							display="block"
							variant="caption"
							style={{ fontSize: '0.65rem' }}
						>
							<FormattedMessage id="Extensions.ShareMachine.Dialog.Link" defaultMessage="Link" />
						</Typography>
						<TextField
							type="text"
							fullWidth
							value={`${getHostOrigin()}/${PATH}/${sharedLink.id}`}
							InputLabelProps={{ shrink: true }}
							InputProps={{ readOnly: true }}
						/>
					</React.Fragment>
				) : null}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="primary">
					<FormattedMessage id="Extensions.Button.Close" defaultMessage="Close" />
				</Button>
				<Button color="primary" disabled={!!sharedLink.id} onClick={share}>
					<FormattedMessage id="Extensions.ShareMachine.Dialog.Button.Create" defaultMessage="Create" />
				</Button>
				<Button color="primary" disabled={!sharedLink.id} onClick={unshare}>
					<FormattedMessage id="Extensions.Dialog.Button.Clear" defaultMessage="Delete" />
				</Button>
			</DialogActions>
		</Dialog>
	);
};



// WEBPACK FOOTER //
// /home/travis/build/cedalo/streamsheets-pro-features/packages/webui-extensions/src/sharemachine/ShareMachineDialog.js